/**
 * Загрузчик Yandex Maps API через CDN скрипт
 * Гарантирует однократную загрузку и кеширование
 */

declare global {
  interface Window {
    ymaps3?: any
  }
}

let loadPromise: Promise<any> | null = null

/**
 * Загрузка Yandex MapsConfigs.tsx API через CDN скрипт
 * @param apiKey - API ключ от Yandex MapsConfigs.tsx
 * @returns Promise с объектом ymaps3
 */
export const loadYandexMapsScript = async (apiKey: string): Promise<any> => {
  console.log('🔄 [Yandex] Начало загрузки API...')

  // Если уже загружено - возвращаем
  if (window.ymaps3) {
    console.log('✅ [Yandex] API уже загружен, используем кеш')
    await window.ymaps3.ready
    console.log('✅ [Yandex] API ready')

    return window.ymaps3
  }

  // Если идёт загрузка - ждём её
  if (loadPromise) {
    console.log('⏳ [Yandex] Загрузка уже идёт, ждём...')

    return loadPromise
  }

  // Начинаем загрузку
  const scriptUrl = `https://api-maps.yandex.ru/v3/?apikey=${apiKey}&lang=ru_RU`
  console.log(`🌐 [Yandex] Загружаем скрипт с API ключом: ${apiKey.substring(0, 8)}...`)
  console.log(`🌐 [Yandex] URL: ${scriptUrl}`)

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = scriptUrl
    script.async = true

    // Timeout на случай зависания
    const timeoutId = setTimeout(() => {
      console.error('⏱️ [Yandex] TIMEOUT! Скрипт не загрузился за 30 секунд')
      console.error('⏱️ [Yandex] Откройте Network tab и проверьте запрос к:', scriptUrl)
      loadPromise = null
      reject(new Error('Script load timeout'))
    }, 30000)

    script.onload = async () => {
      clearTimeout(timeoutId)
      console.log('📦 [Yandex] Скрипт загружен')
      try {
        if (!window.ymaps3) {
          console.error('❌ [Yandex] window.ymaps3 === undefined после загрузки!')
          throw new Error('ymaps3 not loaded')
        }
        console.log('⏳ [Yandex] Ожидание ready...')
        await window.ymaps3.ready
        console.log('✅ [Yandex] ymaps3.ready завершён')
        resolve(window.ymaps3)
      } catch (error) {
        console.error('❌ [Yandex] Ошибка после загрузки:', error)
        reject(error)
      }
    }

    script.onerror = error => {
      clearTimeout(timeoutId)
      console.error('❌ [Yandex] Ошибка загрузки скрипта:', error)
      console.error('❌ [Yandex] URL скрипта:', script.src)
      console.error('❌ [Yandex] Проверьте:')
      console.error('  1. API ключ валиден?')
      console.error('  2. Домен разрешён в настройках ключа?')
      console.error('  3. Есть ли интернет соединение?')
      console.error('  4. Network tab - статус запроса к api-maps.yandex.ru?')
      loadPromise = null
      reject(new Error('Failed to load Yandex MapsConfigs.tsx script'))
    }

    document.head.appendChild(script)
    console.log('📝 [Yandex] Скрипт добавлен в <head>')
  })

  return loadPromise
}

/**
 * Инициализация reactify
 * Вызывать ПОСЛЕ загрузки скрипта
 * @param React - объект React
 * @param ReactDOM - объект ReactDOM
 * @returns объект с reactify и компонентами
 */
export const initReactify = async (React: any, ReactDOM: any) => {
  console.log('🔄 [Yandex] Инициализация reactify...')

  if (!window.ymaps3) {
    throw new Error('ymaps3 not loaded. Call loadYandexMapsScript first')
  }

  console.log('📦 [Yandex] Импорт @yandex/ymaps3-reactify...')
  const ymaps3React = await window.ymaps3.import('@yandex/ymaps3-reactify')
  console.log('✅ [Yandex] Reactify импортирован')

  console.log('🔗 [Yandex] Привязка к React и ReactDOM...')
  const reactify = ymaps3React.reactify.bindTo(React, ReactDOM)
  console.log('✅ [Yandex] Reactify привязан')

  console.log('🔧 [Yandex] Получение компонентов...')
  const components = reactify.module(window.ymaps3)
  console.log('✅ [Yandex] Компоненты получены:', Object.keys(components))

  return {
    reactify,
    components,
  }
}
